/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect} from 'react';
import RootManager from '../client/RootManager';
import useReseller from './resellers/useReseller';
import {DLSThemes} from './types';
import useThemeContext from './useThemeContext';

/**
 * Generates a unique identifier for a theme's font stylesheet link element.
 * The identifier follows the pattern `dls-fonts-{themeName}` and is used to
 * query and manage font stylesheet elements in the DOM.
 *
 * @param   theme The DLS theme for which to generate an identifier.
 * @returns A unique string identifier for the theme's font stylesheet.
 */
const generateId = (theme: DLSThemes) => (
  `dls-fonts-${theme}`
);

/**
 * Determines the selected and previous theme based on the current theme.
 * This utility function simplifies theme transition logic by returning both
 * the active theme and the theme that should be replaced.
 *
 * @param   theme The current DLS theme.
 * @returns An object containing `selectedTheme` (the active theme) and
 *          `previousTheme` (the theme to be replaced).
 */
const getThemes = (theme: DLSThemes) => {
  const selectedTheme = DLSThemes[theme];
  const previousTheme = (
    selectedTheme === DLSThemes.current ? DLSThemes.legacy : DLSThemes.current
  );
  return {selectedTheme, previousTheme};
};

/**
 * Manages the loading and unloading of theme-specific font stylesheets in
 * the DOM. This function removes the previous theme's font stylesheet and
 * loads the new theme's fonts by creating and appending a link element to
 * the document head. If the theme's fonts are already loaded, no action is
 * taken.
 *
 * @param   theme The DLS theme for which to load fonts.
 * @returns void
 */
const resolveThemeFonts = (theme: DLSThemes) => {
  const {selectedTheme, previousTheme} = getThemes(theme);
  const previousThemeId = generateId(previousTheme);
  const selectedThemeId = generateId(selectedTheme);
  RootManager.getContainer(previousThemeId)?.remove();
  const currentThemeStyles = RootManager.getContainer(selectedThemeId);
  if (!currentThemeStyles) {
    const link = document.createElement('link');
    link.id    = selectedThemeId;
    link.rel   = 'stylesheet';
    link.href  = `/themes/${selectedTheme}/fonts.css`;
    document.head.appendChild(link);
  }
};

/**
 * Manages dynamic loading and unloading of theme-specific font stylesheets
 * based on the current DLS theme selection. This component ensures that only
 * the active theme's fonts are loaded in the DOM, automatically removing the
 * previous theme's fonts when a theme switch occurs.
 *
 * The component subscribes to the `currentTheme` signal and responds to theme
 * changes by loading the corresponding font CSS file from
 * `/public/themes/{theme}/fonts.css`. Font stylesheets are identified by
 * unique link element IDs for reliable querying and removal during theme
 * transitions.
 *
 * ```typescript
 *   import {DLSThemeFontLoader} from '@ion/dls/src/theme/DLSThemeFontLoader';
 *   ...
 *
 *   const App = () => (
 *     <>
 *       <DLSThemeFontLoader />
 *       <YourApplicationContent />
 *     </>
 *   );
 * ```
 *
 * @returns `null` - This component does not render any DOM elements.
 */
const DLSThemeFontLoader = () => {
  const {isReseller} = useReseller();
  const {theme, multiThemeEnabled} = useThemeContext();

  useEffect(() => {
    if (!isReseller && multiThemeEnabled) {
      resolveThemeFonts(theme);
    }
  }, [isReseller, theme, multiThemeEnabled]);
  return null;
};

export default React.memo(DLSThemeFontLoader);
